import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Button,
  Badge,
  Text,
  Spinner,
  EmptyState,
  Pagination,
  Filters,
  ChoiceList,
  Box,
  Icon,
  InlineGrid,
  ButtonGroup,
  Tooltip,
  ProgressBar,
  Avatar,
  InlineStack,
  BlockStack,
  useIndexResourceState
} from '@shopify/polaris';
import {
  CircleTickMajor,
  PauseMajor,
  ChecklistAlternateMajor,
  ListMajor,
  ViewMajor,
  PlayMajor,
  CancelMajor
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useShopifyNavigation } from '../hooks/useShopifyNavigation';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import api from '../utils/axiosConfig';

function EmailJourneys() {
  const navigate = useNavigate();
  const navigateWithContext = useShopifyNavigation();
  const { t, i18n } = useTranslation();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState([]);
  
  // Selection state for bulk actions
  const resourceName = {
    singular: 'journey',
    plural: 'journeys',
  };

  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange,
  } = useIndexResourceState(journeys);
  
  useEffect(() => {
    // Wait longer for App Bridge to initialize and URL to be fixed
    const timer = setTimeout(() => {
      loadJourneys();
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  const loadJourneys = async (page = 1, filters = {}) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filters.status && { status: filters.status })
      });
      
      const response = await api.get(`/email-journeys?${params}`);
      
      setJourneys(response.data.journeys || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error loading journeys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = () => {
    const newFilters = {
      status: statusFilter.length > 0 ? statusFilter[0] : null
    };
    
    loadJourneys(1, newFilters);
  };

  const handleClearFilters = () => {
    setStatusFilter([]);
    loadJourneys(1);
  };

  const pauseJourney = async (orderId) => {
    try {
      await api.put(`/email-journeys/${orderId}/pause`);
      loadJourneys(pagination.page);
    } catch (error) {
      console.error('Error pausing journey:', error);
    }
  };

  const resumeJourney = async (orderId) => {
    try {
      await api.put(`/email-journeys/${orderId}/resume`);
      loadJourneys(pagination.page);
    } catch (error) {
      console.error('Error resuming journey:', error);
    }
  };

  const cancelJourney = async (orderId) => {
    if (!confirm(t('emailJourneys.confirmCancel'))) {
      return;
    }
    
    try {
      await api.put(`/email-journeys/${orderId}/cancel`, {
        reason: t('emailJourneys.cancelReason')
      });
      loadJourneys(pagination.page);
    } catch (error) {
      console.error('Error cancelling journey:', error);
    }
  };

  // Bulk actions
  const handleBulkPause = useCallback(async () => {
    try {
      await Promise.all(
        selectedResources.map(id => 
          api.put(`/email-journeys/${id}/pause`)
        )
      );
      loadJourneys(pagination.page);
    } catch (error) {
      console.error('Error bulk pausing journeys:', error);
    }
  }, [selectedResources, pagination.page]);

  const handleBulkResume = useCallback(async () => {
    try {
      await Promise.all(
        selectedResources.map(id => 
          api.put(`/email-journeys/${id}/resume`)
        )
      );
      loadJourneys(pagination.page);
    } catch (error) {
      console.error('Error bulk resuming journeys:', error);
    }
  }, [selectedResources, pagination.page]);

  const handleBulkCancel = useCallback(async () => {
    if (!confirm(`${t('emailJourneys.confirmCancel')} (${selectedResources.length} journeys)`)) {
      return;
    }
    
    try {
      await Promise.all(
        selectedResources.map(id => 
          api.put(`/email-journeys/${id}/cancel`, {
            reason: t('emailJourneys.cancelReason')
          })
        )
      );
      loadJourneys(pagination.page);
    } catch (error) {
      console.error('Error bulk cancelling journeys:', error);
    }
  }, [selectedResources, pagination.page, t]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const locale = i18n.language === 'pt' ? ptBR : enUS;
    const dateFormat = i18n.language === 'pt' ? 'dd/MM/yyyy HH:mm' : 'MM/dd/yyyy HH:mm';
    return format(new Date(date), dateFormat, { locale });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { status: 'success', content: t('emailJourneys.status.active') },
      paused: { status: 'warning', content: t('emailJourneys.status.paused') },
      completed: { status: 'info', content: t('emailJourneys.status.completed') },
      cancelled: { status: 'critical', content: t('emailJourneys.status.cancelled') }
    };
    
    return statusMap[status] || { status: 'new', content: status };
  };

  const getProgressPercentage = (currentDay) => {
    return Math.round((currentDay / 20) * 100);
  };

  // Modern row renderer for IndexTable
  const rowMarkup = journeys.map((journey, index) => (
    <IndexTable.Row
      id={journey.order_id}
      key={journey.order_id}
      selected={selectedResources.includes(journey.order_id)}
      position={index}
    >
      <IndexTable.Cell>
        <InlineStack gap="300" align="start">
          <Avatar
            customer
            size="sm"
            name={journey.orders?.customers?.name || 'Customer'}
          />
          <BlockStack gap="100">
            <Text variant="bodyMd" fontWeight="semibold" as="span">
              {journey.orders?.shopify_order_name || 'N/A'}
            </Text>
            <Text variant="bodySm" as="span" color="subdued">
              {journey.orders?.customers?.name || 'N/A'}
            </Text>
          </BlockStack>
        </InlineStack>
      </IndexTable.Cell>
      
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {journey.customer_email}
        </Text>
      </IndexTable.Cell>
      
      <IndexTable.Cell>
        <BlockStack gap="200">
          <Badge {...getStatusBadge(journey.status)} />
          <Box paddingBlockStart="100">
            <BlockStack gap="100">
              <Text variant="bodySm" as="p" color="subdued">
                {t('emailJourneys.progress', { current: journey.current_day, percentage: getProgressPercentage(journey.current_day) })}
              </Text>
              <ProgressBar 
                progress={getProgressPercentage(journey.current_day)} 
                size="small"
                color={journey.status === 'active' ? 'primary' : 'subdued'}
              />
            </BlockStack>
          </Box>
        </BlockStack>
      </IndexTable.Cell>
      
      <IndexTable.Cell>
        <BlockStack gap="100">
          <Text variant="bodySm" as="p">
            <Text variant="bodySm" as="span" color="subdued">Last: </Text>
            {formatDate(journey.last_email_sent)}
          </Text>
          <Text variant="bodySm" as="p">
            <Text variant="bodySm" as="span" color="subdued">Next: </Text>
            {formatDate(journey.next_email_date)}
          </Text>
        </BlockStack>
      </IndexTable.Cell>
      
      <IndexTable.Cell>
        <ButtonGroup>
          <Tooltip content={t('emailJourneys.actions.view')}>
            <Button 
              size="micro" 
              icon={ViewMajor}
              onClick={() => navigate(`/order/${journey.order_id}`)}
            />
          </Tooltip>
          
          {journey.status === 'active' && (
            <Tooltip content={t('emailJourneys.actions.pause')}>
              <Button 
                size="micro" 
                icon={PauseMajor}
                onClick={() => pauseJourney(journey.order_id)}
              />
            </Tooltip>
          )}
          
          {journey.status === 'paused' && (
            <Tooltip content={t('emailJourneys.actions.resume')}>
              <Button 
                size="micro" 
                icon={PlayMajor}
                variant="primary"
                onClick={() => resumeJourney(journey.order_id)}
              />
            </Tooltip>
          )}
          
          {(journey.status === 'active' || journey.status === 'paused') && (
            <Tooltip content={t('emailJourneys.actions.cancel')}>
              <Button 
                size="micro" 
                icon={CancelMajor}
                tone="critical"
                onClick={() => cancelJourney(journey.order_id)}
              />
            </Tooltip>
          )}
        </ButtonGroup>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const filters = [
    {
      key: 'status',
      label: t('common.status'),
      filter: (
        <ChoiceList
          title={t('emailJourneys.statusFilter')}
          titleHidden
          choices={[
            { label: t('emailJourneys.status.active'), value: 'active' },
            { label: t('emailJourneys.status.paused'), value: 'paused' },
            { label: t('emailJourneys.status.completed'), value: 'completed' },
            { label: t('emailJourneys.status.cancelled'), value: 'cancelled' }
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
          allowMultiple={false}
        />
      ),
      shortcut: true,
    }
  ];

  const appliedFilters = [];
  
  if (statusFilter.length > 0) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${statusFilter[0]}`,
      onRemove: () => {
        setStatusFilter([]);
        handleFiltersChange();
      }
    });
  }

  // Promotional bulk actions for IndexTable
  const promotedBulkActions = [
    {
      content: t('emailJourneys.actions.pause'),
      onAction: handleBulkPause,
      disabled: !selectedResources.length || 
        !journeys.some(j => selectedResources.includes(j.order_id) && j.status === 'active')
    },
    {
      content: t('emailJourneys.actions.resume'),
      onAction: handleBulkResume,
      disabled: !selectedResources.length || 
        !journeys.some(j => selectedResources.includes(j.order_id) && j.status === 'paused')
    },
  ];

  const bulkActions = [
    {
      content: t('emailJourneys.actions.cancel'),
      onAction: handleBulkCancel,
      destructive: true,
      disabled: !selectedResources.length || 
        !journeys.some(j => selectedResources.includes(j.order_id) && 
          (j.status === 'active' || j.status === 'paused'))
    },
  ];

  if (loading) {
    return (
      <Page title={t('emailJourneys.title')}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p" color="subdued">
                  {t('emailJourneys.loading')}
                </Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('emailJourneys.title')}
      subtitle={t('emailJourneys.subtitle')}
    >
      <Layout>
        {/* Stats Cards - Modern Design with Icons */}
        <Layout.Section>
          <InlineGrid columns={{ xs: '1', sm: '2', md: '4' }} gap="400">
            {/* Active Journeys */}
            <Card padding="400">
              <Box>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    backgroundColor: '#e3f1df', 
                    padding: '8px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text variant="headingMd" as="h3" fontWeight="semibold">
                      {journeys.filter(j => j.status === 'active').length}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('emailJourneys.activeJourneys')}
                    </Text>
                  </div>
                </div>
              </Box>
            </Card>
            
            {/* Paused Journeys */}
            <Card padding="400">
              <Box>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    backgroundColor: '#fff4e6', 
                    padding: '8px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={PauseMajor} color="warning" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text variant="headingMd" as="h3" fontWeight="semibold">
                      {journeys.filter(j => j.status === 'paused').length}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('emailJourneys.pausedJourneys')}
                    </Text>
                  </div>
                </div>
              </Box>
            </Card>
            
            {/* Completed Journeys */}
            <Card padding="400">
              <Box>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    backgroundColor: '#e0f5ff', 
                    padding: '8px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={ChecklistAlternateMajor} color="highlight" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text variant="headingMd" as="h3" fontWeight="semibold">
                      {journeys.filter(j => j.status === 'completed').length}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('emailJourneys.completedJourneys')}
                    </Text>
                  </div>
                </div>
              </Box>
            </Card>
            
            {/* Total Journeys */}
            <Card padding="400">
              <Box>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    backgroundColor: '#f3f4f6', 
                    padding: '8px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={ListMajor} color="base" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text variant="headingMd" as="h3" fontWeight="semibold">
                      {journeys.length}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('emailJourneys.totalJourneys')}
                    </Text>
                  </div>
                </div>
              </Box>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Journeys Table */}
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Filters
                filters={filters}
                appliedFilters={appliedFilters}
                onClearAll={handleClearFilters}
                onFiltersChange={handleFiltersChange}
              />
            </div>
            
            {journeys.length === 0 ? (
              <Box padding="400">
                <EmptyState
                  heading={t('emailJourneys.noJourneysFound')}
                  action={{
                    content: t('emailJourneys.viewOrders'),
                    onAction: () => navigate('/orders')
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <Text variant="bodyMd" as="p">
                    {t('emailJourneys.noJourneysDescription')}
                  </Text>
                </EmptyState>
              </Box>
            ) : (
              <>
                <IndexTable
                  resourceName={resourceName}
                  itemCount={journeys.length}
                  selectedItemsCount={
                    allResourcesSelected ? 'All' : selectedResources.length
                  }
                  onSelectionChange={handleSelectionChange}
                  promotedBulkActions={promotedBulkActions}
                  bulkActions={bulkActions}
                  headings={[
                    { title: t('emailJourneys.tableHeaders.order') },
                    { title: t('emailJourneys.tableHeaders.email') },
                    { title: t('emailJourneys.tableHeaders.status') },
                    { title: 'Timeline' },
                    { title: t('emailJourneys.tableHeaders.actions') },
                  ]}
                  loading={loading}
                  emptyState={
                    <EmptyState
                      heading={t('emailJourneys.noJourneysFound')}
                      action={{
                        content: t('emailJourneys.viewOrders'),
                        onAction: () => navigate('/orders')
                      }}
                      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                    >
                      <Text variant="bodyMd" as="p">
                        {t('emailJourneys.noJourneysDescription')}
                      </Text>
                    </EmptyState>
                  }
                >
                  {rowMarkup}
                </IndexTable>
                
                {pagination.totalPages > 1 && (
                  <Box padding="400">
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center'
                    }}>
                      <Pagination
                        hasPrevious={pagination.page > 1}
                        onPrevious={() => loadJourneys(pagination.page - 1)}
                        hasNext={pagination.page < pagination.totalPages}
                        onNext={() => loadJourneys(pagination.page + 1)}
                        label={`Page ${pagination.page} of ${pagination.totalPages}`}
                      />
                    </div>
                  </Box>
                )}
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default EmailJourneys;